<!-- Component for displaying a standard modal -->
<template>
	<TransitionRoot
		as="template"
		:show="open"
	>
		<Dialog
			as="div"
			class="relative z-60"
			@close="onClose"
		>
			<TransitionChild
				as="template"
				enter="ease-out duration-300"
				enter-from="opacity-0"
				enter-to="opacity-100"
				leave="ease-in duration-200"
				leave-from="opacity-100"
				leave-to="opacity-0"
			>
				<div class="fixed inset-0 bg-gray-500/75 transition-opacity"></div>
			</TransitionChild>

			<div class="fixed inset-4 z-50">
				<div
					class="flex h-full items-end justify-center p-4 text-center sm:items-center sm:p-0"
				>
					<TransitionChild
						as="template"
						enter="ease-out duration-300"
						enter-from="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
						enter-to="opacity-100 translate-y-0 sm:scale-100"
						leave="ease-in duration-200"
						leave-from="opacity-100 translate-y-0 sm:scale-100"
						leave-to="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
					>
						<DialogPanel
							class="relative max-h-[calc(100vh-2rem)] transform overflow-hidden rounded-lg bg-white text-left shadow-xl transition-all sm:my-8 sm:w-full"
							:class="[modalSizes[size], $attrs.class]"
						>
							<button
								v-if="canClose"
								type="button"
								@click="onClose"
								title="Cancel"
								class="stroke-secondary-300 absolute top-4 right-4 z-50 h-6 w-6 hover:stroke-slate-700"
							>
								<XMarkIcon />
							</button>
							<div class="max-h-[calc(100vh-2rem)] overflow-y-auto">
								<FormKit
									type="form"
									@submit="onSubmit"
									:actions="false"
								>
									<div class="bg-white px-4 pt-12 pb-4 sm:p-6 sm:pb-4">
										<div
											v-if="modalTitle"
											class="mt-3 text-center sm:mt-0 sm:text-left"
										>
											<DialogTitle
												class="text-secondary-900 text-center font-sans text-base leading-normal font-bold"
											>
												{{ modalTitle }}
											</DialogTitle>
										</div>
										<slot></slot>
									</div>
									<div
										v-if="$slots.buttons"
										class="gap-2 px-4 py-3 sm:flex sm:flex-row sm:px-6"
									>
										<slot name="buttons"></slot>
									</div>

									<div
										v-if="$slots.footer"
										class="px-4 pb-6 sm:flex sm:flex-row sm:px-6"
									>
										<slot name="footer"></slot>
									</div>
								</FormKit>
							</div>
						</DialogPanel>
					</TransitionChild>
				</div>
			</div>
		</Dialog>
	</TransitionRoot>
</template>

<script setup lang="ts">
import { FormKit } from '@formkit/vue'
import {
	Dialog,
	DialogPanel,
	DialogTitle,
	TransitionChild,
	TransitionRoot,
} from '@headlessui/vue'
import { XMarkIcon } from '@heroicons/vue/24/solid'

type ModalSize = 'standard' | 'medium' | 'large'

const modalSizes: Record<ModalSize, string> = {
	standard: 'sm:max-w-sm',
	medium: 'sm:max-w-[572px]',
	large: 'sm:max-w-5xl',
}

const emit = defineEmits<{
	submit: [formData: any]
	close: []
}>()

const props = withDefaults(
	defineProps<{
		canClose?: boolean
		modalTitle?: string
		size?: ModalSize

		// OnSubmit passed as a function so it can return a promise to FormKit
		onSubmit?: (formData: any) => any
	}>(),
	{
		canClose: true,
		size: 'standard',
	}
)

const onClose = () => {
	if (props.canClose) {
		open.value = false
		emit('close')
	}
}

const open = defineModel<boolean>('open')
</script>
